<template>
  <!-- TECHDEBT: add functionality to provide filter, per-page, etc from props -->
  <div class="mc-table">
    <slot
      name="before-table"
      :isSelected="!!selectedIdsLength"
      :showDeleteModal="showDeleteModal"
      :isBusy="isBusy"
    >
      <div
        v-if="typeof customHandlers?.handleAdd !== 'undefined' || (selectedIdsLength && hasDelete)"
        class="clearfix mb-3"
      >
        <div class="float-end d-flex gap-2">
          <McButton
            v-if="selectedIdsLength && hasDelete"
            variant="secondary-red"
            :text="$t('global.delete')"
            icon="icon-outline_trash"
            class="mb-2 ms-auto"
            @click="showDeleteModal"
          />
          <McButton
            v-if="typeof customHandlers?.handleAdd !== 'undefined'"
            variant="primary"
            class="mb-2 ms-auto"
            :disabled="isBusy"
            :text="$t('global.action.create')"
            icon="icon-fill_plus"
            @click="handleAdd"
          />
        </div>
      </div>
    </slot>
    <div v-if="hasEmptyState && !isBusy && items.length === 0">
      <slot name="emptyState" />
    </div>
    <div v-else :class="withCard ? 'card' : ''">
      <div :class="withCard ? 'card-body' : ''">
        <div class="d-flex justify-content-between align-items-center flex-wrap before-table">
          <h6 v-if="selectedIdsLength" class="mc-row-check mt-0">
            <span class="badge btn-success">{{ selectedIdsLength }}</span>
            {{ $t('global.object.selectedRow') }}
            <a class="color-primary" @click="handleUnselectAll">{{
              $t('global.action.deselect')
            }}</a>
          </h6>

          <!-- Priority info display -->
          <h6 v-if="priorityIdsLength && hasPriority" v-show="false" class="mc-priority-info mt-0">
            <span class="badge btn-warning">{{ priorityIdsLength }}</span>
            {{ $t('global.object.priorityRow') || 'Priority items' }}
            <a class="color-primary" @click="handleUnselectAllPriority">{{
              $t('global.action.clearPriority') || 'Clear all'
            }}</a>
          </h6>

          <slot name="customDisplayItems" />
          <div class="d-flex justify-content-end flex-grow-1 gap-2 flex-wrap slot-item-end">
            <slot
              name="viewManager"
              :handleSetDataFields="handleSetDataFields"
              :dataFields="dataFields"
              :initialFields="initialFields"
            />
            <slot name="beforeSearchBar" />
            <b-input-group v-if="searchable" size="sm" class="mc-has-search" @click="focusSearch">
              <template #prepend>
                <i class="ps-2 mc-icon icon-fill_search"></i>
              </template>
              <b-form-input
                ref="searchInput"
                type="search"
                :placeholder="`${$t('global.search')}...`"
                :value="search"
                @input="handleSearchChange"
              />
            </b-input-group>
            <slot name="afterSearchBar" />
          </div>
        </div>
        <div
          :class="{
            'table-responsive border border-1 mc-table-overflow': true,
            'text-nowrap': !isResponsive,
          }"
          :style="tableMaxHeight ? { maxHeight: tableMaxHeight, overflowY: 'auto' } : {}"
        >
          <b-table
            ref="table-ref"
            :sticky-header="stickyHeader"
            :busy="isBusy"
            :items="items"
            :fields="computedFields"
            :per-page="hasLimit ? limit : 0"
            :bordered="bordered"
            :foot-clone="footClone"
            :selectable="hasSelectableRow"
            :tbody-tr-class="rowClass"
            :select-mode="multiple ? 'multi' : 'single'"
            fixed
            show-empty
            class="b-table"
            @row-clicked="handleRowClicked"
          >
            <!-- Loading -->
            <template #table-busy>
              <div class="__busy">
                <b-spinner class="align-middle mb-2" />
                <strong>{{ $t('global.text.loading') }}...</strong>
              </div>
            </template>

            <!-- Empty Result -->
            <template #empty>
              <div class="text-center my-4">
                {{ $t('global.text.noData') }}
              </div>
            </template>

            <!-- Slot: Header Checkbox -->
            <template v-if="hasSelectAll" #head(checkbox)>
              <div class="form-check" style="width: 10px">
                <b-form-checkbox
                  v-model="selectedAll"
                  :disabled="isBusy || disableSelectAll"
                  @change="handleSelectAll"
                />
              </div>
            </template>

            <!-- Slot: Header Priority -->
            <template v-if="hasPriority" #head(priority)>
              <div style="width: 10px">
                <i
                  v-if="hasPrioritySelectAll"
                  :class="{
                    'mc-icon': true,
                    'icon-fill_star': prioritySelectedAll,
                    'icon-outline_star': !prioritySelectedAll,
                    'priority-star': true,
                    'priority-star--active': prioritySelectedAll,
                    'priority-star--inactive': !prioritySelectedAll,
                    'priority-select-all': true,
                  }"
                  :disabled="isBusy || disablePrioritySelectAll"
                  @click="handlePrioritySelectAll"
                />
              </div>
            </template>

            <!-- Slot: Header Data -->
            <template #head()="data">
              <div @click="handleSortChange(data)">
                {{ data.label }}
                <template v-if="data.field.isSortable">
                  <div v-if="showSortIcon(data, 'asc')" class="sort-icon-up">
                    <i class="mc-icon icon-outline_chevron-up"></i>
                  </div>
                  <div v-if="showSortIcon(data, 'desc')" class="sort-icon-down">
                    <i class="mc-icon icon-outline_chevron-down"></i>
                  </div>
                </template>
              </div>
            </template>

            <!-- Slot: Cell Checkbox -->
            <template #cell(checkbox)="{ item }">
              <div class="form-check">
                <b-form-checkbox
                  :checked="selectedIds[item.id]"
                  :disabled="isDisableRowCheckbox(item)"
                  @change="(event) => handleSelect(event, item)"
                />
              </div>
            </template>

            <!-- Slot: Cell Priority -->
            <template #cell(priority)="{ item }">
              <div class="priority-cell">
                <i
                  v-b-tooltip.hover.bottom="getPriorityTooltip(item)"
                  :class="{
                    'mc-icon': true,
                    'icon-fill_star': priorityIds[item[trackBy]],
                    'icon-outline_star': !priorityIds[item[trackBy]],
                    'priority-star': true,
                    'priority-star--active': priorityIds[item[trackBy]],
                    'priority-star--inactive': !priorityIds[item[trackBy]],
                  }"
                  :disabled="isDisablePriority(item)"
                  @click="handlePriorityToggle(item)"
                />
              </div>
            </template>

            <!-- Slot: Cells on first column -->
            <template #[`cell(${fields[0].key})`]="{ index, field, value, item }">
              <a
                v-b-tooltip.hover.right="getTooltipValue(field, value)"
                :style="field?.tdStyle"
                :class="{
                  'td__overflow-ellipsis': !field.isEllipsis,
                  'td__overflow-ellipsis-two-lines': field.isEllipsis,
                  'clickable-field': field.isRedirectable,
                  'text-primary': !hasViewManager,
                  'non-clickable': !isFirstRowClickable,
                }"
                :href="field.hasOpenNewTab ? handleOpenNewTab(field, item) : null"
                @click.prevent="handleClickRow(field, item)"
              >
                {{ value }}
              </a>
            </template>

            <!-- Slot: All Cells -->
            <template #cell()="{ index, field, value }">
              <template v-if="typeof value === 'number'">
                <div :style="field?.tdStyle" class="td__overflow-ellipsis">
                  {{ value | formatNumber }}
                </div>
              </template>

              <template v-else>
                <div
                  v-if="field.overFlowThreeLines"
                  v-b-tooltip="getTooltipValue(field, value)"
                  :style="field?.tdStyle"
                  class="td__overflow-ellipsis-three-lines"
                >
                  {{ value }}
                </div>
                <div
                  v-else-if="field.isEllipsis"
                  v-b-tooltip="getTooltipValue(field, value)"
                  :style="field?.tdStyle"
                  class="td__overflow-ellipsis-two-lines"
                >
                  {{ value }}
                </div>
                <div
                  v-else
                  v-b-tooltip="getTooltipValue(field, value)"
                  :style="field?.tdStyle"
                  :class="{ 'td__overflow-ellipsis': !field.isResponsive }"
                >
                  {{ value }}
                </div>
              </template>
            </template>

            <!-- Slot: Slot override from parent component -->
            <template v-for="(index, name) in $scopedSlots" #[name]="data">
              <slot :name="name" v-bind="data"></slot>
            </template>
          </b-table>
        </div>
        <div
          v-if="hasLimit || hasPagination"
          class="mc-table-footer d-flex flex-wrap flex-md-nowrap"
        >
          <div v-if="hasLimit" class="mc-limit">
            <div class="__limit">
              <div>{{ $t('global.show') }}</div>
              <b-form-select
                class="form-select form-select-sm __limit--dropdown"
                size="sm"
                :disabled="isBusy"
                :options="limitOptions"
                :value="limit"
                @input="handleLimitChange"
              />
              <div>{{ $t('global.object.data').toLowerCase() }}.</div>
            </div>

            <div class="__info">
              <strong>{{ startData }}</strong> - <strong>{{ endData }}</strong>
              {{ $t('global.object.of') }}
              <strong>{{ totalCount }}</strong>
              .
            </div>
          </div>
          <div v-if="hasPagination" class="mc-pagination-container">
            <!-- NOTE: totalCount set default value to be a very large number -->
            <!-- to prevent initialPage not to be set in pagination component -->
            <b-pagination
              :disabled="isBusy"
              :per-page="limit"
              :total-rows="totalCount || limit * page"
              :value="page"
              @change="handlePageChange"
            />
          </div>
        </div>
      </div>
    </div>
    <b-modal
      id="mc-modal-delete"
      ref="mc-modal-delete"
      class="mc-modal"
      :title="$t('global.action.delete')"
      size="md"
      centered
      scrollable
    >
      <div class="card-body p-2">
        <p>
          {{ $t('global.confirmDelete1') }}
          <strong>{{ selectedIdsLength }}</strong>
          {{ $t('global.confirmDelete2') }}
        </p>
      </div>
      <template #modal-footer="{ cancel }">
        <div class="d-flex">
          <McButton
            variant="secondary"
            :text="$t('global.action.cancel')"
            class="me-2"
            @click="cancel()"
          />
          <McButton variant="primary" :text="$t('global.action.delete')" @click="handleDelete" />
        </div>
      </template>
    </b-modal>
  </div>
</template>

<script>
import { debounce } from '@/helpers/debounce'
import McButton from '@/components/McButton'
import cloneDeep from 'lodash/cloneDeep'
import { TableHelper } from '@/mixin/table'

const cloneFields = (fields) =>
  cloneDeep(fields).map((field) => ({
    ...field,
    shown: typeof field.shown !== 'undefined' ? field.shown : true,
  }))

export default {
  components: {
    McButton,
  },
  mixins: [TableHelper],
  props: {
    freezeColumnBetween: {
      type: Boolean,
      default: false,
    },
    fields: {
      type: Array,
      required: true,
    },
    apiClient: {
      type: Object,
      required: true,
    },
    initialItems: {
      type: Array,
      default: () => [],
    },
    useClientSide: {
      type: Boolean,
      default: false,
    },
    limitOptions: {
      type: Array,
      default: () => [10, 25, 50, 100],
    },
    initialPage: {
      type: Number,
      default: 1,
    },
    initialLimit: {
      type: Number,
      default: 10,
    },
    initialSearch: {
      type: String,
      default: '',
    },
    initialSorts: {
      type: Array,
      default: () => [],
    },
    hasDelete: {
      type: Boolean,
      default: true,
    },
    // TODO: can be changed to `hasBulkDelete` for consistency
    bulkDeleteApi: {
      type: Boolean,
      default: false,
    },
    customHandlers: {
      type: Object,
      default: () => {},
    },
    searchable: {
      type: Boolean,
    },
    bordered: {
      type: Boolean,
      default: false,
    },
    footClone: {
      type: Boolean,
      default: false,
    },
    busy: {
      type: Boolean,
    },
    withCard: {
      type: Boolean,
    },
    hasCheckbox: {
      type: Boolean,
      default: true,
    },
    stickyCheckbox: {
      type: Boolean,
      default: true,
    },
    hasSelectAll: {
      type: Boolean,
      default: true,
    },
    stickyHeader: {
      type: Boolean,
    },
    disableStickyOnMobile: {
      type: Boolean,
      default: false,
    },
    stickyMobileBreakpoint: {
      type: Number,
      default: 768,
    },
    isResponsive: {
      type: Boolean,
      default: false,
    },
    'hasEmit:deleteError': {
      type: Boolean,
      default: false,
    },
    'hasEmit:deleteSuccess': {
      type: Boolean,
      default: false,
    },
    'hasEmit:deleteErrorV2': {
      type: Boolean,
      default: false,
    },
    activeFilters: {
      type: Object,
      default: () => {},
    },
    hasViewManager: {
      type: Boolean,
      default: false,
    },
    disableSelect: {
      type: Boolean,
      default: false,
    },
    disableSelectAll: {
      type: Boolean,
      default: false,
    },
    hasSelectableRow: {
      type: Boolean,
      default: false,
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    trackBy: {
      type: String,
      default: 'id',
    },
    hasTooltip: {
      type: Boolean,
      default: false,
    },
    isFirstRowClickable: {
      type: Boolean,
      default: true,
    },
    hasEmptyState: {
      type: Boolean,
      default: false,
    },
    initialSelectedIds: {
      type: Object,
      default: () => {},
    },
    // New props for priority feature
    hasPriority: {
      type: Boolean,
      default: false,
    },
    stickyPriority: {
      type: Boolean,
      default: true,
    },
    disablePriority: {
      type: Boolean,
      default: false,
    },
    initialPriorityIds: {
      type: Object,
      default: () => {},
    },
    hasPrioritySelectAll: {
      type: Boolean,
      default: false,
    },
    disablePrioritySelectAll: {
      type: Boolean,
      default: false,
    },
    rowClass: {
      type: Function,
      default: () => {},
    },
    hasPagination: {
      type: Boolean,
      default: true,
    },
    hasLimit: {
      type: Boolean,
      default: true,
    },
    // Props untuk infinite scroll
    infiniteScrollBatchSize: {
      type: Number,
      default: 10,
    },
    infiniteScrollInitialItems: {
      type: Number,
      default: 10,
    },
    // Prop untuk tinggi container tabel
    tableMaxHeight: {
      type: String,
      default: null,
    },
  },
  emits: [
    'deleteError',
    'deleteSuccess',
    'listRes',
    'handleSelectValue',
    'handlePriorityChange',
    'handlePrioritySelectAll',
    'available-change',
    'handlePageChange',
  ],
  data() {
    return {
      page: this.initialPage,
      limit: this.initialLimit,
      search: this.initialSearch,
      totalCount: 0,
      isBusy: true,
      sorts: this.initialSorts,
      serverItems: [],
      serverIds: [],
      rowUpdate: {},
      selectedIds: { ...this.initialSelectedIds },
      selectedIdsLength: 0,
      selectedAll: false,
      dataFields: cloneFields(this.fields),
      hasActiveFilters: false,
      // New data for priority feature
      priorityIds: { ...this.initialPriorityIds },
      priorityIdsLength: 0,
      prioritySelectedAll: false,
      // Penanda request untuk menghindari race condition getData
      requestId: 0,
      // Data untuk infinite scroll
      itemsToShow: this.infiniteScrollInitialItems,
      isLoadingMore: false,
      scrollContainer: null,
      isMobileViewport: false,
    }
  },
  computed: {
    shouldDisableStickyOnMobile() {
      return this.disableStickyOnMobile && this.isMobileViewport
    },
    filteredItems() {
      if (!this.useClientSide || !this.search) return this.currentSourceItems

      const searchTerm = this.search.toLowerCase()

      return this.currentSourceItems.filter((item) => {
        return this.dataFields.some((field) => {
          let value = this.getItemValue(item, field.key)
          if (value == null) return false
          value = String(value).toLowerCase()
          return value.includes(searchTerm)
        })
      })
    },
    sortedItems() {
      if (!this.useClientSide || this.sorts.length === 0) return this.filteredItems

      return [...this.filteredItems].sort((a, b) => {
        for (const sort of this.sorts) {
          const aVal = this.getItemValue(a, sort.key)
          const bVal = this.getItemValue(b, sort.key)

          let comparison = 0
          if (aVal < bVal) comparison = -1
          if (aVal > bVal) comparison = 1

          if (comparison !== 0) {
            return sort.order === 'asc' ? comparison : -comparison
          }
        }
        return 0
      })
    },
    paginatedItems() {
      if (!this.useClientSide || !this.hasPagination) return this.sortedItems

      const start = (this.page - 1) * this.limit
      const end = start + this.limit
      return this.sortedItems.slice(start, end)
    },
    currentSourceItems() {
      return this.useClientSide ? this.initialItems : this.serverItems
    },
    items() {
      if (this.useClientSide) {
        if (this.useClientSide && !this.hasPagination && !this.hasLimit) {
          // Infinite scroll mode: show itemsToShow items
          return this.sortedItems.slice(0, this.itemsToShow)
        }
        return this.paginatedItems
      }
      return this.serverItems
    },
    ids() {
      if (this.useClientSide) {
        return this.sortedItems.map((item) => item[this.trackBy])
      }
      return this.serverIds
    },
    startData() {
      if (this.useClientSide) {
        return this.sortedItems.length === 0 ? 0 : (this.page - 1) * this.limit + 1
      }
      return this.limit * this.page - this.limit + 1
    },
    endData() {
      if (this.useClientSide) {
        const end = this.page * this.limit
        return end > this.sortedItems.length ? this.sortedItems.length : end
      }
      const _endData = this.limit * this.page
      return this.totalCount < _endData ? this.totalCount : _endData
    },
    computedFields() {
      const fields = this.dataFields
        .filter((field) => field.shown)
        .map((field) => {
          const normalizedField = this.normalizeFieldStickyBehavior(field)

          if (field.isEllipsis) {
            return {
              ...normalizedField,
              thStyle: { width: '250px', minWidth: '250px', ...normalizedField.thStyle },
            }
          }
          return normalizedField
        })

      if (this.hasCheckbox) {
        fields.unshift({
          key: 'checkbox',
          label: '',
          stickyColumn: this.stickyCheckbox,
        })
      }

      // Add priority column if enabled
      if (this.hasPriority) {
        const priorityIndex = this.hasCheckbox ? 1 : 0
        fields.splice(priorityIndex, 0, {
          key: 'priority',
          label: '',
          stickyColumn: this.shouldDisableStickyOnMobile ? false : this.stickyPriority,
        })
      }

      if (this.freezeColumnBetween && !this.shouldDisableStickyOnMobile)
        this.applyStickyColumnToPrev(fields, this.hasPriority ? 2 : 1, 5)

      return fields
    },
    initialFields() {
      return cloneFields(this.fields)
    },
  },
  watch: {
    initialItems: {
      handler() {
        if (this.useClientSide) {
          this.isBusy = false
          this.totalCount = this.initialItems.length
          // Reset itemsToShow ketika data berubah
          this.itemsToShow = this.infiniteScrollInitialItems
        }
      },
      immediate: true,
      deep: true,
    },
    search() {
      if (this.useClientSide && !this.hasPagination && !this.hasLimit) {
        // Reset itemsToShow ketika pencarian berubah
        this.itemsToShow = this.infiniteScrollInitialItems
      }
    },
    useClientSide(_newVal) {
      this.toggleScrollListener()
    },
    hasPagination() {
      this.toggleScrollListener()
    },
    hasLimit() {
      this.toggleScrollListener()
    },
    activeFilters() {
      if (!this.useClientSide) {
        this.getData({
          limit: this.limit,
          page: 1,
        })
        this.hasActiveFilters = true
      }
    },
    fields: {
      handler: function (val) {
        this.dataFields = cloneFields(val)
      },
      deep: true,
    },
    priorityIds: {
      handler: function (val) {
        this.priorityIdsLength = Object.values(val).filter(Boolean).length

        // Update prioritySelectedAll based on current items
        if (this.items.length > 0) {
          const currentPageIds = this.items.map((item) => item[this.trackBy])
          const currentPagePriorityCount = currentPageIds.filter((id) => val[id]).length
          this.prioritySelectedAll =
            currentPagePriorityCount === currentPageIds.length && currentPageIds.length > 0
        }
      },
      deep: true,
      immediate: true,
    },
    initialPriorityIds: {
      handler: function (val) {
        this.priorityIds = { ...val }
      },
      deep: true,
    },
    initialSelectedIds: {
      handler: function (val) {
        if (this.items.length > 0) {
          this.selectedIds = { ...val }
          this.selectedIdsLength = Object.values(val).filter(Boolean).length
          this.selectedAll = this.selectedIdsLength === this.totalCount && this.totalCount > 0
        }
      },
      deep: true,
    },
  },
  async mounted() {
    this.checkViewport()
    window.addEventListener('resize', this.checkViewport)
    await this.$nextTick()
    if (!this.useClientSide) {
      await this.getData({
        limit: this.limit,
        page: this.page,
        search: this.search || undefined,
        sorts: this.sorts.map((sort) => `${sort.key} ${sort.order}`),
      })
    }
    // Tambahkan scroll listener untuk infinite scroll
    if (this.useClientSide && !this.hasPagination && !this.hasLimit) {
      this.addScrollListener()
    }
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.checkViewport)
    this.removeScrollListener()
  },
  methods: {
    checkViewport() {
      if (typeof window === 'undefined') return
      this.isMobileViewport = window.innerWidth < this.stickyMobileBreakpoint
    },
    removeStickyStyle(style = '') {
      if (!style || !this.shouldDisableStickyOnMobile) return style

      return style
        .replace(/(?:^|;)position\s*:\s*sticky\s*;?/gi, ';')
        .replace(/(?:^|;)left\s*:\s*[^;]+;?/gi, ';')
        .replace(/(?:^|;)right\s*:\s*[^;]+;?/gi, ';')
        .replace(/(?:^|;)z-index\s*:\s*[^;]+;?/gi, ';')
        .replace(/;;+/g, ';')
        .replace(/^;|;$/g, '')
        .trim()
    },
    normalizeStickyAttr(attr) {
      if (!attr || !this.shouldDisableStickyOnMobile) return attr

      const normalizedStyle = this.removeStickyStyle(attr.style)

      if (!normalizedStyle) {
        const { style: _style, ...restAttr } = attr
        return Object.keys(restAttr).length > 0 ? restAttr : undefined
      }

      return {
        ...attr,
        style: normalizedStyle,
      }
    },
    normalizeFieldStickyBehavior(field) {
      if (!this.shouldDisableStickyOnMobile) return field

      return {
        ...field,
        stickyColumn: false,
        thAttr: this.normalizeStickyAttr(field.thAttr),
        tdAttr: this.normalizeStickyAttr(field.tdAttr),
      }
    },
    toggleScrollListener() {
      if (this.useClientSide && !this.hasPagination && !this.hasLimit) {
        this.addScrollListener()
      } else {
        this.removeScrollListener()
      }
    },
    addScrollListener() {
      this.scrollContainer = this.$el.querySelector('.mc-table-overflow') || window
      this.scrollContainer.addEventListener('scroll', this.handleScroll)
    },
    removeScrollListener() {
      if (this.scrollContainer) {
        this.scrollContainer.removeEventListener('scroll', this.handleScroll)
      }
    },
    handleScroll() {
      if (this.isLoadingMore) return

      const container = this.scrollContainer
      let scrollTop, scrollHeight, clientHeight

      if (container === window) {
        scrollTop = window.pageYOffset || document.documentElement.scrollTop
        scrollHeight = document.documentElement.scrollHeight
        clientHeight = document.documentElement.clientHeight
      } else {
        scrollTop = container.scrollTop
        scrollHeight = container.scrollHeight
        clientHeight = container.clientHeight
      }

      // Ketika scroll hampir sampai bawah (100px sebelum)
      if (scrollTop + clientHeight >= scrollHeight - 100) {
        this.loadMoreItems()
      }
    },
    loadMoreItems() {
      if (this.itemsToShow >= this.sortedItems.length) return

      this.isLoadingMore = true

      // Simulasi loading untuk feedback user
      setTimeout(() => {
        this.itemsToShow += this.infiniteScrollBatchSize
        this.isLoadingMore = false
      }, 300)
    },
    getItemValue(item, key) {
      if (!key) return item
      const keys = key.split('.')
      let value = item
      for (const k of keys) {
        if (value == null) return null
        value = value[k]
      }
      return value
    },
    focusSearch() {
      this.$refs.searchInput.focus()
    },
    showDeleteModal() {
      this.$refs['mc-modal-delete'].show()
    },
    showSortIcon(data, order) {
      const currentColumnSort = this.sorts.find((sort) => sort.key === data.field.key)
      if (!currentColumnSort) {
        return true
      }

      return currentColumnSort.key === data.field.key && currentColumnSort.order === order
    },
    // TECHDEBT: Only supports resourceful format
    // getData methods can be improved to support user-supplied data
    async getData(opts, silent = false) {
      if (!silent) {
        this.isBusy = true
      }
      const currentId = ++this.requestId

      const res = await this.apiClient.get(opts)
      if (this.$listeners?.listRes) {
        this.$emit('listRes', res)
      }

      // Jika selama menunggu response sudah ada request lain yang lebih baru,
      // abaikan hasil yang ini untuk mencegah data lama menimpa data terbaru
      if (currentId !== this.requestId || !res || res?.errors) {
        if (!silent) {
          this.isBusy = false
        }
        return
      }

      this.serverItems = res.data.paginated_result
      this.serverIds = res.data.ids
      this.page = res.metadata.page
      this.totalCount = res.metadata.total_count
      this.$emit('available-change', true)

      if (res.data.paginated_result.length === 0 && !this.search) {
        this.page = 1
        this.$emit('available-change', false)
        if (this.hasActiveFilters) {
          this.$emit('available-change', true)
        }
      }

      this.hasActiveFilters = false
      if (!silent) {
        this.isBusy = false
      }

      // Apply initialSelectedIds after data is loaded
      if (this.initialSelectedIds && Object.keys(this.initialSelectedIds).length > 0) {
        this.selectedIds = { ...this.initialSelectedIds }
        this.selectedIdsLength = Object.values(this.initialSelectedIds).filter(Boolean).length
        this.selectedAll = this.selectedIdsLength === this.totalCount && this.totalCount > 0
      }
    },
    handleSearchChange: debounce(async function (_search) {
      this.search = _search
      this.page = 1

      if (!this.useClientSide) {
        const opts = {
          limit: this.limit,
          search: _search != '' ? _search : undefined,
          page: 1,
          sorts: this.sorts.map((sort) => `${sort.key} ${sort.order}`),
        }
        await this.getData(opts)
      }

      if (this.$refs.searchInput && this.$refs.searchInput.$el) {
        this.$refs.searchInput.$el.focus()
      }
    }, 500),
    handleClearSearch() {
      this.handleSearchChange(undefined)
    },
    async handleLimitChange(_limit) {
      this.limit = _limit
      this.page = 1

      if (!this.useClientSide) {
        await this.getData({
          limit: _limit,
          search: this.search != '' ? this.search : undefined,
          page: 1,
          sorts: this.sorts.map((sort) => `${sort.key} ${sort.order}`),
        })
      }
    },
    async handlePageChange(newPage) {
      this.page = newPage

      if (!this.useClientSide) {
        const opts = {
          page: newPage,
          search: this.search != '' ? this.search : undefined,
          limit: this.limit,
          sorts: this.sorts.map((sort) => `${sort.key} ${sort.order}`),
        }
        await this.getData(opts)
      }
      this.$emit('handlePageChange')
    },
    async handleSortChange(data) {
      // If this function is called without data params,
      // remove sorting (revert to default)
      if (!data) {
        this.sorts = []
        return
      }

      // If field not sortable or If currently editing a field, disable sort
      if (!data?.field?.isSortable) return

      // Get index of current column sort, if any
      const currentColumnSortIndex = this.sorts.findIndex((sort) => sort.key === data.field.key)
      // Get the sorting data (key, order)
      const currentColumnSort = this.sorts[currentColumnSortIndex]
      const newSorts = [...this.sorts]
      // This switch case is used to toggle between these conditions:
      // default - asc - desc - default (repeat)
      switch (currentColumnSort?.order) {
        case 'asc':
          newSorts[currentColumnSortIndex] = {
            key: currentColumnSort.key,
            order: 'desc',
          }
          break

        case 'desc':
          newSorts.splice(currentColumnSortIndex, 1)
          break

        default:
          newSorts.push({ key: data.field.key, order: 'asc' })
          break
      }

      this.sorts = [...newSorts]
      this.page = 1
      await this.$nextTick()

      if (!this.useClientSide) {
        await this.getData({
          limit: this.limit,
          page: 1,
          search: this.search != '' ? this.search : undefined,
          sorts: this.sorts.map((sort) => `${sort.key} ${sort.order}`),
        })
      }
    },
    async handleDelete() {
      const _selected = Object.keys(this.selectedIds).filter((key) => this.selectedIds[key] == true)

      let res = null

      if (this.bulkDeleteApi) {
        res = await this.apiClient.delete({
          ids: _selected,
        })
      } else {
        res = await Promise.all(_selected.map((key) => this.apiClient.delete(key)))
      }

      if (this.search) this.handleClearSearch()
      await this.getData({
        limit: this.limit,
        page: 1,
      })
      this.handleSortChange()
      this.handleUnselectAll()
      this.$refs['mc-modal-delete'].hide()

      if (this['hasEmit:deleteErrorV2']) {
        this.$emit('deleteErrorV2', res, _selected)
        return
      }

      if (res.errors) {
        if (this['hasEmit:deleteError']) {
          this.$emit('deleteError', res)
          return
        }

        this.$mcToast.error('Gagal menghapus data.')
        return
      }

      if (this['hasEmit:deleteSuccess']) {
        this.$emit('deleteSuccess', res)
        return
      }

      this.$mcToast.success({
        component: 'McToastDynamicBody',
        props: {
          contents: [
            {
              tag: 'strong',
              text: _selected.length,
              class: '__blue-highlight-text',
            },
            { tag: 'span', text: this.$t('global.alert.text.delete') },
          ],
        },
      })
    },
    handleAdd() {
      if (this.customHandlers?.handleAdd) this.customHandlers.handleAdd()
    },
    handleSelect(checked, item) {
      const updatedItem = { ...item, checked }
      this.selectedIds = { ...this.selectedIds, [item.id]: checked }
      this.selectedItems = { ...this.selectedItems, [item.id]: updatedItem }
      if (checked) {
        this.selectedIdsLength += 1
      } else {
        this.selectedIdsLength -= 1
      }

      if (this.selectedIdsLength === this.totalCount) {
        this.selectedAll = true
      } else if (this.selectedIdsLength === 0) {
        this.selectedAll = false
        this.selectedIds = {}
        this.$emit('handleSelectValue', {})
        return
      } else {
        this.selectedAll = false
      }

      this.$emit('handleSelectValue', this.selectedIds, this.selectedItems)
    },
    handleSelectAll(event) {
      if (event) {
        this.selectedIds = Object.fromEntries(this.ids.map((key) => [key, true]))
        this.$emit('handleSelectValue', this.selectedIds)
        this.selectedIdsLength = this.totalCount
        this.selectedAll = true
      } else {
        this.handleUnselectAll()
      }
    },
    handleUnselectAll() {
      this.selectedIds = {}
      this.$emit('handleSelectValue', {})
      this.selectedAll = false
      this.selectedIdsLength = 0
    },
    handleSelectSpecificIds(ids) {
      if (!ids || !Array.isArray(ids) || ids.length === 0) {
        this.handleUnselectAll()
        return
      }
      this.selectedIds = Object.fromEntries(ids.map((id) => [id, true]))
      this.selectedIdsLength = ids.length
      this.selectedAll =
        this.totalCount > 0 &&
        this.selectedIdsLength === this.totalCount &&
        ids.every((id) => this.ids.includes(id))
      this.$emit('handleSelectValue', this.selectedIds)
    },
    handleClickRow(field, item) {
      if (field.isRedirectable && this.customHandlers?.handleClickRow)
        this.customHandlers.handleClickRow(item)
    },
    handleOpenNewTab(field, item) {
      if (field.hasOpenNewTab && this.customHandlers?.handleOpenNewTab) {
        return this.customHandlers?.handleOpenNewTab(item)
      }
    },
    handleSetDataFields(fields) {
      this.dataFields = cloneDeep(fields)
    },
    isDisableRowCheckbox(item) {
      let disableItemFromCustomHandler = false
      if (this.customHandlers?.isItemSelectDisabled) {
        disableItemFromCustomHandler = this.customHandlers.isItemSelectDisabled(item)
      }

      return this.isBusy || this.selectedIds[item.id]
        ? false
        : this.disableSelect || disableItemFromCustomHandler
    },
    handleRowClicked(item, index) {
      if (!this.hasSelectableRow) return
      const trackBy = this.trackBy

      // Last checked value before the user clicked on the row
      const checked = this.selectedIds[item[trackBy]]

      // To protect when chexbox state (selectedIds)
      // is not synced with b-table select state
      // Force select/unselect to match the checkbox state
      const toggleRowSelection = !checked ? 'unselectRow' : 'selectRow'
      this.$refs['table-ref'][toggleRowSelection](index)

      if (this.multiple) {
        this.$set(this.selectedIds, item[trackBy], !checked)
      } else {
        this.selectedIds = {
          [item[trackBy]]: !checked,
        }
      }
      this.$emit('handleSelectValue', this.selectedIds)
    },
    ResetSelects() {
      this.selectedIds = {}
      this.selectedIdsLength = 0

      // Also reset priorities if needed
      if (this.hasPriority) {
        this.resetPriorities()
      }
    },
    // New methods for priority feature
    handlePriorityToggle(item) {
      if (this.isDisablePriority(item)) return

      const itemId = item[this.trackBy]
      const currentPriority = this.priorityIds[itemId]

      this.$set(this.priorityIds, itemId, !currentPriority)

      // Emit priority change event
      this.$emit('handlePriorityChange', {
        item,
        priority: !currentPriority,
        priorityIds: { ...this.priorityIds },
      })

      // Call custom handler if provided
      if (this.customHandlers?.handlePriorityChange) {
        this.customHandlers.handlePriorityChange(item, !currentPriority)
      }
    },

    handlePrioritySelectAll() {
      if (this.isBusy || this.disablePrioritySelectAll) return

      const currentPageIds = this.items.map((item) => item[this.trackBy])
      const newPriorityIds = { ...this.priorityIds }

      if (this.prioritySelectedAll) {
        // Remove priority from all current page items
        currentPageIds.forEach((id) => {
          newPriorityIds[id] = false
        })
        this.prioritySelectedAll = false
      } else {
        // Add priority to all current page items (excluding disabled ones)
        currentPageIds.forEach((id) => {
          const item = this.items.find((item) => item[this.trackBy] === id)
          if (!this.isDisablePriority(item)) {
            newPriorityIds[id] = true
          }
        })
        this.prioritySelectedAll = true
      }

      this.priorityIds = newPriorityIds

      // Emit priority select all event
      this.$emit('handlePrioritySelectAll', {
        selectAll: !this.prioritySelectedAll,
        priorityIds: { ...this.priorityIds },
        currentPageIds,
      })

      // Call custom handler if provided
      if (this.customHandlers?.handlePrioritySelectAll) {
        this.customHandlers.handlePrioritySelectAll(!this.prioritySelectedAll, currentPageIds)
      }
    },

    isDisablePriority(item) {
      let disableItemFromCustomHandler = false
      if (this.customHandlers?.isItemPriorityDisabled) {
        disableItemFromCustomHandler = this.customHandlers.isItemPriorityDisabled(item)
      }

      return this.isBusy || this.disablePriority || disableItemFromCustomHandler
    },

    resetPriorities() {
      this.priorityIds = {}
      this.priorityIdsLength = 0
      this.prioritySelectedAll = false
    },

    setPriorityIds(priorityIds) {
      this.priorityIds = { ...priorityIds }
    },

    handleUnselectAllPriority() {
      this.priorityIds = {}
      this.prioritySelectedAll = false

      this.$emit('handlePriorityChange', {
        item: null,
        priority: false,
        priorityIds: {},
        action: 'clear_all',
      })

      if (this.customHandlers?.handleClearAllPriority) {
        this.customHandlers.handleClearAllPriority()
      }
    },

    getPriorityTooltip(item) {
      const itemId = item[this.trackBy]
      const isPriority = this.priorityIds[itemId]

      if (this.isDisablePriority(item)) {
        return null // Tidak menampilkan tooltip jika disabled
      }

      return isPriority
        ? this.$t('global.action.removePriority') || 'Hapus Prioritas'
        : this.$t('global.action.setPriority') || 'Jadikan Prioritas'
    },

    getTooltipValue(field, value) {
      if (!value || typeof value !== 'string') return null

      const tooltipLength = field.columnTooltipLength

      if (field.overFlowThreeLines) {
        return field.columnHasTooltip && value.length > (tooltipLength || 60) ? value : null
      }

      if (field.isEllipsis) {
        // threshold 30 is more safe for 2 lines of 250px
        return value.length > (tooltipLength || 30) ? value : null
      }

      if (field.columnHasTooltip) {
        return value.length > (tooltipLength || 20) ? value : null
      }

      return null
    },
  },
}
</script>

<style lang="scss" scoped>
@import './style.scss';
</style>
